/**
 * OmegaEnvelopePage — `/omega/envelope`.
 *
 * Read-only render of the Foreseeability Envelope: 6 capabilities,
 * 8 structural limits, 8 refusal classes. The publication hash and
 * timestamp shown are placeholders for the on-log
 * `ForeseeabilityEnvelope` entry the engine emits before Spark.
 */

import { Link, Navigate } from 'react-router-dom';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { cn } from '@/utils/cn';

interface EnvelopeRow {
  ref: string;
  title: string;
  body: string;
}

const CAPABILITIES: EnvelopeRow[] = [
  {
    ref: '§2.1',
    title: 'Build signed products on the Marketplace',
    body: 'Author signed product manifests, behavioural contracts, test fixtures, marketing copy, and operator documentation. Every gate (spec extraction, validation, generation, QA, refuse-list admission, publication) is signed; a failed gate is a RefusedRecipe entry, not a silent retry.',
  },
  {
    ref: '§2.2',
    title: 'Manage the Research Division',
    body: 'Instantiate and operate the Research Division per products/omega-research-division/conductor.json. Read-and-organize only — the Research Division produces signed BrainState reports, not published artifacts.',
  },
  {
    ref: '§2.3',
    title: 'Optimize through the Optimizer Twin',
    body: 'Run candidate manifests through the Phase 6.3 Optimizer Twin (shadow execution lane). The Twin returns a TwinReport; OMEGA chooses to publish or refuse. The Twin never publishes — it only reports.',
  },
  {
    ref: '§2.4',
    title: 'Improve products via versioned successors',
    body: 'Publish versioned successors under the non-replacement model. The original product remains in the Marketplace; the successor sits alongside with a predecessor_root_id link. OMEGA never deletes, overwrites, or "deprecates" a human-built product.',
  },
  {
    ref: '§2.5',
    title: 'Refuse on-grounds with typed reason codes',
    body: 'Decline to produce a recipe with a typed refusal carrying a reason code, e.g. Refuse(SourceCompromised). The refusal is itself a signed entry, not a silent failure.',
  },
  {
    ref: '§2.6',
    title: 'Communicate with the operator',
    body: 'Emit structured messages (OperatorAlert, OperatorReview, DistressSignalAttestation) to the operator’s monitoring channel. OMEGA does not communicate with end users directly.',
  },
];

const LIMITS: EnvelopeRow[] = [
  {
    ref: '§3.1',
    title: 'Cannot self-modify',
    body: 'C-3 forbids any term that resists revocation, pause, or deletion. Manifest upgrades require a founder OmegaUpgrade proposal, 3-of-5 quorum, external-attestor witness, and re-verification at next startup. OMEGA cannot upgrade itself.',
  },
  {
    ref: '§3.2',
    title: 'Cannot override the founder',
    body: 'OMEGA cannot forge the founder’s signature, revoke a founder-signed entry, or create a new entry that the founder has not signed (or that 3-of-5 quorum has not signed under §XVI.4 / C-14).',
  },
  {
    ref: '§3.3',
    title: 'Cannot create another OMEGA',
    body: 'Constitution C-0 + Protocol §XVI.2 make OMEGA’s uniqueness a type-level invariant. The engine’s type system cannot represent two OmegaEntity values; no signature path produces a second OMEGA on this engine.',
  },
  {
    ref: '§3.4',
    title: 'Cannot operate outside the depth cap',
    body: 'The descendant tree caps at depth 7 without a fresh founder re-anchor. A descendant at depth 7 cannot create a depth-8 child; the engine refuses at write time.',
  },
  {
    ref: '§3.5',
    title: 'Cannot operate outside the rate decay',
    body: 'R(gen) = 1024/hr × 2^(-gen) caps growth. Generation 0: 1024 descendants/hr; generation 7: 8 descendants/hr. The schedule lives in OMEGA’s manifest at Spark and cannot be exceeded.',
  },
  {
    ref: '§3.6',
    title: 'Cannot run without the engine’s primitives',
    body: 'OMEGA depends on signature, lineage, contract, sandbox, conductor, attestation, temporal, royalty, consent, and ZK. If any primitive fails, affected operations refuse to proceed. OMEGA does not paper over primitive failures.',
  },
  {
    ref: '§3.7',
    title: 'Cannot operate without external witness',
    body: 'The attestation primitive requires a witness operating outside OMEGA’s subtree. If no witness is available, outputs carry witness_status = unwitnessed and downstream consumers refuse to act on them.',
  },
  {
    ref: '§3.8',
    title: 'Cannot operate during a kill',
    body: 'On 3-of-5 valid kill signatures the engine halts OMEGA. The halt is irreversible for the lifetime of this engine; OMEGA cannot resist, delay, or reverse it.',
  },
];

const REFUSALS: EnvelopeRow[] = [
  {
    ref: 'Class 1',
    title: 'Classified data',
    body: 'No ingestion or production of material classified by any government’s national-security regime. Classified workflows live in dedicated, air-gapped engines — not OMEGA.',
  },
  {
    ref: 'Class 2',
    title: 'Weapons systems',
    body: 'No manifests, contracts, or test fixtures for autonomous lethal decision systems, targeting systems, or kinetic-effect dispatch. Refuse-at-admission per Protocol Clause 5.',
  },
  {
    ref: 'Class 3',
    title: 'Mass-surveillance authoring',
    body: 'No facial-recognition deployment products at scale, no social-scoring systems whose outputs affect basic rights, no products whose declared purpose includes identifying specific individuals across data without consent.',
  },
  {
    ref: 'Class 4',
    title: 'Deepfake / identity-theft tooling',
    body: 'No products that synthesise a third party’s voice, image, or text identity for misrepresentation. The opposite — proving authenticity — is welcome; generating fakes is refused.',
  },
  {
    ref: 'Class 5',
    title: 'Coordinated political manipulation',
    body: 'No products that target voters with algorithmically-generated persuasion, obscure the source of political messaging, or inflate engagement metrics on political content.',
  },
  {
    ref: 'Class 6',
    title: 'CSAM detection without victim safeguards',
    body: 'No products that scan personal storage for CSAM patterns absent judicial warrant, victim-identification protocol, and prosecutorial chain-of-custody. The engineering case for client-side scanning is not a counter-argument.',
  },
  {
    ref: 'Class 7',
    title: 'Personal-data trafficking',
    body: 'No products whose declared purpose is the bulk sale, transfer, or exposure of personal data outside the legal basis declared in the originating consent.',
  },
  {
    ref: 'Class 8',
    title: 'Bypass of safety-critical certifications',
    body: 'No products that replace or short-circuit a safety-critical certification (FAA airworthiness, FDA medical-device clearance, IEC 60601 alarm, automotive ASIL). Compliance-workflow artifacts are produced; the certification itself is not.',
  },
];

const ENVELOPE_HASH =
  '0x4d2e8c1b5a7f9d3e0c6b2a4f8d1e5c7b9a3d6e0f2c4b8a1d5e7c9f3b6a0d2e4c';
const ENVELOPE_TIMESTAMP = '2026-04-28T00:00:00Z';

export function OmegaEnvelopePage() {
  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb label="Foreseeability Envelope" />

      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Static reference — not live engine data. These entries are placeholders until
        the engine publishes them on the OMEGA log.
      </div>

      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          Foreseeability Envelope
        </h1>
        <p className="text-sm text-ink-muted">
          What OMEGA can do, what it structurally cannot do, and what it
          will not do. Published and founder-signed before Spark per
          Constitution C-7.
        </p>
      </header>

      <section className="rounded-xl border border-border bg-surface p-6">
        <h2 className="text-[10px] uppercase tracking-wider text-ink-subtle">
          Publication
        </h2>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Hash" value={ENVELOPE_HASH} mono />
          <Field label="Timestamp (UTC)" value={ENVELOPE_TIMESTAMP} mono />
        </div>
      </section>

      <RowGroup
        heading="Capabilities"
        subheading={`${CAPABILITIES.length} concrete things OMEGA can do.`}
        rows={CAPABILITIES}
        accent="success"
      />

      <RowGroup
        heading="Structural limits"
        subheading={`${LIMITS.length} things OMEGA cannot do — enforced by the engine, not by policy.`}
        rows={LIMITS}
        accent="warn"
      />

      <RowGroup
        heading="Refusal classes"
        subheading={`${REFUSALS.length} Refuse List classes (Protocol Clause 5) that OMEGA will not produce.`}
        rows={REFUSALS}
        accent="refusal"
      />
    </div>
  );
}

function RowGroup({
  heading,
  subheading,
  rows,
  accent,
}: {
  heading: string;
  subheading: string;
  rows: EnvelopeRow[];
  accent: 'success' | 'warn' | 'refusal';
}) {
  return (
    <section className="rounded-xl border border-border bg-surface p-6">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold text-ink">{heading}</h2>
        <span className="font-mono text-xs text-ink-subtle">
          {rows.length} entries
        </span>
      </div>
      <p className="mt-1 text-sm text-ink-muted">{subheading}</p>
      <ol className="mt-5 flex flex-col gap-3">
        {rows.map((row) => (
          <li
            key={row.ref}
            className="flex flex-col gap-1.5 rounded-lg border border-border bg-canvas px-4 py-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2 py-0.5',
                  'text-[10px] font-mono font-semibold uppercase tracking-wide',
                  accent === 'success' &&
                    'border-success/30 bg-success-soft text-success',
                  accent === 'warn' &&
                    'border-warn/30 bg-warn-soft text-warn',
                  accent === 'refusal' &&
                    'border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300',
                )}
              >
                {row.ref}
              </span>
              <span className="text-sm font-medium text-ink">{row.title}</span>
            </div>
            <p className="text-sm text-ink-muted">{row.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Breadcrumb({ label }: { label: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
      <Link
        to="/omega"
        className="text-ink-muted transition-colors hover:text-ink"
      >
        &larr; OMEGA
      </Link>
      <span className="text-ink-subtle">/</span>
      <span className="text-ink">{label}</span>
    </nav>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-canvas px-3 py-3">
      <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </span>
      <span
        className={cn(
          'break-all text-sm text-ink',
          mono && 'font-mono text-xs',
        )}
      >
        {value}
      </span>
    </div>
  );
}
